<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations, animateValue } from '$lib/utils/animations';
  import LineChart from '../../../lib/components/LineChart.svelte';
  import Slideshow from '$lib/components/Slideshow.svelte';
  import { Gallery } from 'flowbite-svelte';
  import VideoPlayer from '$lib/components/VideoPlayer.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import FeatureCell from '$lib/components/FeatureCell.svelte';
  import IndustryTag from '$lib/components/IndustryTag.svelte';
  import ProjectHeroFullWidth from '$lib/components/ProjectHeroFullWidth.svelte';
  import Quote from '$lib/components/Quote.svelte';
  import { reveal } from 'svelte-reveal';
  import ChallengeSolution from '$lib/components/ChallengeSolution.svelte';
  
  let scrollY = 0;
  let translateY = 0;
  
  // Make the parallax effect reactive
  $: translateY = scrollY * 0.5;

  let titleContent: HTMLElement;
  let viewsCount = 0;
  let languagesCount = 0;
  let yearsCount = 0;

  onMount(() => {
    // Setup number animations
    const metricsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateValue(0, 50, 2000, (val) => viewsCount = val);
          animateValue(0, 2, 1000, (val) => languagesCount = val);
          animateValue(0, 5, 1500, (val) => yearsCount = val);
          metricsObserver.disconnect();
        }
      });
    });

    const metricsSection = document.querySelector('.metrics-section');
    if (metricsSection) {
      metricsObserver.observe(metricsSection);
    }

    // Setup scroll animations
    setupProjectAnimations();

    // Cleanup function
    return () => {
      metricsObserver.disconnect();
    };
  });

  // Mock data for the charts
  const accuracyData = [98, 99, 99.5, 99.7, 99.8, 99.9, 99.9, 99.9, 99.9];
  const speedData = [2.5, 2.2, 2.0, 1.8, 1.6, 1.5, 1.4, 1.3, 1.2];
  const efficiencyData = [85, 88, 90, 92, 94, 95, 96, 97, 98];

  const projectSlides = [
    "https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fscreenshot_landscape_4.21d51cb3.png&w=3840&q=75",
    { 
      content: "Made to Measure", 
      class: "text-6xl font-extrabold bg-blue-300"
    },
    { 
      content: "AI-Driven", 
      class: "text-6xl font-extrabold bg-red-300"
    },
    "https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fscreenshot_landscape_5.5cd84341.png&w=3840&q=75"
  ];

  let scrollOffset = 0;
  
  function updateScrollOffset() {
    const elements = document.querySelectorAll('[style*="--scroll-offset"]');
    elements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      const rect = htmlEl.getBoundingClientRect();
      const offset = (window.innerHeight - rect.top) * 0.5;
      htmlEl.style.setProperty('--scroll-offset', offset.toString());
    });
  }

  onMount(() => {
    window.addEventListener('scroll', updateScrollOffset);
    return () => window.removeEventListener('scroll', updateScrollOffset);
  });
</script>


<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <ProjectHeroFullWidth
    title="Made to Measure"
    client="Kulturstiftung des Bundes"
    clientLogo="https://field-notes.berlin/sites/default/files/styles/16x9/public/2023-04/Kulturstiftung%20des%20Bundes.png?itok=9c35VDsU"
    industry="Culture"
    product="A personalized data and AI-driven storytelling experience."
    heroMedia={{
      type: 'video',
      src: "https://vz-6b226b53-3c8.b-cdn.net/4a6745e8-0954-44ec-8649-46f95639c69d/playlist.m3u8"
    }}
    metrics={[
      { value: 50000, label: "Monthly Users" },
      { value: 2, label: "Languages" },
      { value: 5, label: "Years of Data" }
    ]}
    roles={['<strong>Creative Lead</strong><br />Shaping product narrative, UX, and data-driven storytelling']}
  >
    <img
      slot="award"
      src="https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fiiba.f8be2eb9.png&w=3840&q=75"
      alt="Information is Beautiful Award"
      class="w-24 h-24 object-contain mt-8"
      use:reveal={{ blur: 1 }}
    />
  </ProjectHeroFullWidth>

  <!-- Content -->
  <div class="projects-section w-full">
    <div class="container px-8 md:px-16">
      <!-- Large Text Block -->
      <div class="space-y-2 mb-12 border-b border-black/10 py-6" data-delay="1" use:reveal={{ y: 10 }}>
        <p class="large-text text-black/90">
            Can you reconstruct a person based solely on their digital data trail?
        </p>
      </div>

      <ChallengeSolution
        challengeText="An experience that would make the abstract concept of data privacy tangible by attempting to reconstruct a person's life, habits, and personality solely from five years of Google search data."
        solutionText="An interactive, data- and AI-driven storytelling experience reaching over 50.000 views in the first month."
      />
    </div>

    <!-- Full Width Image Section -->
    <div class="relative w-full overflow-hidden mt-8 md:mt-16 mb-8 md:mb-16">
      <!-- First image scrolling from right -->
      <div class="relative w-full overflow-hidden" data-delay="7" use:reveal={{ y: 10 }}>
        <div style="transform: translateX(calc(40% + {scrollY * -0.7}px))">
          <img 
            src="https://www.nand.io/_next/static/media/screen-reel.48943c8f.jpg" 
            alt="Made to Measure Interface" 
            class="max-w-none h-[30vh] object-cover"
            style="margin-left: -50%;"
          />
        </div>
      </div>

      <!-- Second image scrolling from left - hidden on mobile -->
      <div class="relative w-full overflow-hidden mt-8 hidden md:block" data-delay="8" use:reveal={{ y: 10 }}>
        <div style="transform: translateX(calc(-150% + {scrollY * 0.7}px))">
          <img 
            src="https://www.nand.io/_next/static/media/screen-reel.48943c8f.jpg" 
            alt="Made to Measure Interface" 
            class="max-w-none h-[30vh] object-cover"
            style="margin-left: -50%;"
          />
        </div>
      </div>

      <div class="container px-8 md:px-16 mt-8">
        <div class="border-t border-b border-black/10 py-6">
          <p class="text-sm text-gray-500">
            The interactive installation showing real-time data analysis and psychological profiling.
          </p>
        </div>
      </div>
    </div>

    <!-- Key Features Section -->
    <div class="w-full mb-16">
      <h2 class="text-2xl font-light mb-4">Key Features</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <FeatureCell
          icon="video"
          title="Interactive Video"
          description="Custom video player with seamless video-interactive transitions"
        />
        
        <FeatureCell
          icon="monitor"
          title="Cross-Platform"
          description="Optimized for all platforms (desktop and mobile)"
        />

        <FeatureCell
          icon="database"
          title="Data Analysis"
          description="Internal research tool for analyzing 50,000+ searches and visits"
        />

        <FeatureCell
          icon="activity"
          title="Real-time Visualization"
          description="Real-time data visualization and animation overlays"
        />
      </div>
    </div>

    <!-- Process Section -->
    <section class="py-16 animate-on-view">
      <div class="container mx-auto px-4 max-w-3xl flex flex-col">
        <h2 class="text-2xl font-light mb-2 md:mb-8">Process</h2>
        <p class="text-gray-700 mb-8">
          A key part of our process involved developing custom data visualization tools and software prototypes to validate our concepts and gather insights. This approach allowed us to test different narrative structures and interaction patterns before committing to the final product design.
        </p>
        <img 
          src="/projects/made-to-measure/m2mdatavis.png" 
          alt="Early data visualization prototype" 
          class="rounded-lg shadow-md w-full h-96 object-cover mb-8"
        />
        <p class="text-gray-700 mb-8">
          Through rapid prototyping and iterative testing, we were able to refine the user experience and ensure that the final product would effectively communicate the complex relationship between personal data and digital identity.
        </p>

        <img 
          src="/projects/made-to-measure/layers.png" 
          alt="System architecture layers" 
          class="shadow-md w-full max-w-2xl bg-black mb-8"
        />
      </div>
    </section>

    <!-- Quote Section -->
    <Quote 
      text="The grand finale of the experiment is the encounter between the doppelganger and the original. The tension among all participants in this moment is almost electric."
    />

    <!-- Image Gallery -->
    <section>
      <div class="container">
        <div class="grid grid-cols-1 gap-4">
          <img 
            src="https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fscreenshot_landscape_1.87fcc915.png&w=3840&q=75" 
            alt="Made to Measure interface" 
            class="h-auto max-w-full" 
            use:reveal={{ y: 10 }}
          />
          
          <!-- Local static images with correct paths -->
          <img 
            src="/projects/made-to-measure/profile-timeline.png" 
            alt="Timeline profile view showing digital footprint" 
            class="h-auto max-w-full" 
            use:reveal={{ y: 10 }}
          />
          <img 
            src="/projects/made-to-measure/expert-interview.png" 
            alt="Expert interview about psychological targeting" 
            class="h-auto max-w-full" 
            use:reveal={{ y: 10 }}
          />

          <!-- Additional NAND images -->
          <img 
            src="https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fscreenshot_landscape_4.21d51cb3.png&w=3840&q=75" 
            alt="Made to Measure visualization" 
            class="h-auto max-w-full" 
            use:reveal={{ y: 10 }}
          />
          <img 
            src="https://www.nand.io/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fscreenshot_landscape_6.d10ffbed.png&w=3840&q=75" 
            alt="Made to Measure data visualization" 
            class="h-auto max-w-full" 
            use:reveal={{ y: 10 }}
          />
        </div>
      </div>
    </section>

    <!-- Video Section -->
    <div class="container mx-auto px-4 pt-24 pb-16">
      <div class="aspect-video w-full" use:reveal={{ y: 10 }}>
        <iframe 
          title="Made to Measure - Project Video" 
          src="https://player.vimeo.com/video/592098130?h=89f6d6b7de" 
          class="w-full h-full"
          frameborder="0"    
          allowfullscreen
        ></iframe>
      </div>
    </div>

    <!-- Awards Section -->
    <div class="w-full mb-32">
      <h2 class="text-2xl font-light mt-8 mb-8 px-8">Awards & Recognition</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <FeatureCell
          icon="award"
          title="Media Award DAME 2001"
          description="Winner of Media award DAME 2001"
        />
        
        <FeatureCell
          icon="star"
          title="Information is Beautiful"
          description="Winner of the prestigious Information is Beautiful Award 2022"
        />

        <FeatureCell
          icon="globe"
          title="Ars Electronica 2021"
          description="Premiered at the international media art festival in Linz"
        />

        <FeatureCell
          icon="map-pin"
          title="International Exhibitions"
          description="Exhibited in Vienna, Dresden, and Munich"
        />
      </div>
    </div>
  </div>

  <!-- Project Navigation -->
  <ProjectNav 
    prevProject={{ title: "Pandemieradar", url: "/projects/pandemieradar" }}
    nextProject={{ title: "AOK Design System", url: "/projects/aok-design-system" }}
  />

  <Footer />
</div> 